import React, { useState, useEffect, useCallback } from "react";
import { Box, Stack, Typography, Tabs, Tab, Skeleton, alpha } from "@mui/material";
import { MdScience } from "react-icons/md";
import { FaFlask, FaBoxes, FaCloudDownloadAlt } from "react-icons/fa";
import axios from "axios";
import LabTestsList from "./LabTestsList";
import LabPackagesList from "./LabPackagesList";
import RedcliffeCatalogList from "./RedcliffeCatalogList";
import { getProviderMeta } from "../../../utils/labProviders";

const BASE = "https://api.medstown.com";

const C = {
  primary:   "#0c4a6e",
  secondary: "#0284c7",
  custom:    "#7c3aed",
  warning:   "#d97706",
  success:   "#16a34a",
  pkg:       "#0e7490",
};

const RC = getProviderMeta("redcliffe");

/**
 * The first two tabs are Orange's catalog: stored in our database, priced by an
 * admin per item. The third is Redcliffe's: fetched live from their API, never
 * stored, and read-only — there is no row to price. Keeping them as sibling tabs
 * rather than one merged table is deliberate, because what an admin can DO on
 * each is fundamentally different.
 */
const TABS = [
  { label: "Individual Tests", icon: <FaFlask size={13} />,             tone: C.primary },
  { label: "Packages",         icon: <FaBoxes size={13} />,             tone: C.pkg },
  { label: "Redcliffe (live)", icon: <FaCloudDownloadAlt size={13} />,  tone: RC.tone },
];

const StatPill = ({ label, value, color, loading }) => (
  <Box sx={{
    display: "inline-flex", alignItems: "center", gap: 0.8,
    px: 1.5, py: 0.5, borderRadius: 2,
    border: `1px solid ${alpha(color, 0.2)}`,
    backgroundColor: alpha(color, 0.06),
  }}>
    {loading ? <Skeleton width={20} height={18} /> : (
      <Typography variant="caption" fontWeight={800} sx={{ color, fontSize: "0.8rem" }}>{value}</Typography>
    )}
    <Typography variant="caption" color="text.secondary" fontWeight={500} sx={{ fontSize: "0.7rem" }}>{label}</Typography>
  </Box>
);

export default function LabTests() {
  const [tab, setTab] = useState(0);
  const [stats, setStats]           = useState({ tests: 0, packages: 0, customTests: 0, customPackages: 0, redcliffe: 0 });
  const [statsLoading, setStatsLoading] = useState(false);

  const fetchStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      // Redcliffe's count comes from their live catalog, not our DB. Settled
      // separately so their API being slow or down cannot blank the Orange
      // counts alongside it.
      const [testsRes, pkgsRes, customTestsRes, customPkgsRes, rcRes] = await Promise.all([
        axios.get(`${BASE}/admin/lab/tests?limit=1`),
        axios.get(`${BASE}/admin/lab/packages?limit=1`),
        axios.get(`${BASE}/admin/lab/tests?limit=1&priceFilter=custom`),
        axios.get(`${BASE}/admin/lab/packages?limit=1&priceFilter=custom`),
        axios.get(`${BASE}/admin/lab/redcliffe/status`).catch(() => null),
      ]);
      setStats({
        tests:         testsRes.data.pagination?.total    || 0,
        packages:      pkgsRes.data.pagination?.total     || 0,
        customTests:   customTestsRes.data.pagination?.total  || 0,
        customPackages: customPkgsRes.data.pagination?.total  || 0,
        redcliffe:     rcRes?.data?.count || 0,
      });
    } catch { /* silent */ }
    finally { setStatsLoading(false); }
  }, []);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  return (
    <Box sx={{ mt: 2 }}>
      {/* Header + stat pills */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1} sx={{ mb: 1.5 }}>
        <Typography variant="h6" fontWeight={700} sx={{ color: C.primary }}>
          Lab Tests &amp; Packages
        </Typography>
        <Stack direction="row" spacing={0.8} flexWrap="wrap">
          <StatPill label="Tests"          value={stats.tests}          color={C.primary}   loading={statsLoading} />
          <StatPill label="Packages"       value={stats.packages}       color={C.pkg}       loading={statsLoading} />
          <StatPill label="Custom Priced Tests" value={stats.customTests}  color={C.custom}    loading={statsLoading} />
          <StatPill label="Custom Priced Pkgs"  value={stats.customPackages} color={C.warning} loading={statsLoading} />
          <StatPill label="Redcliffe (live)"    value={stats.redcliffe}    color={RC.tone}     loading={statsLoading} />
        </Stack>
      </Stack>

      {/* Tabs */}
      <Box sx={{
        mb: 1.5,
        bgcolor: alpha(C.primary, 0.03),
        borderRadius: 2,
        p: 0.5,
        border: `1px solid ${alpha(C.primary, 0.08)}`,
        display: "inline-flex",
      }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          TabIndicatorProps={{ style: { display: "none" } }}
          sx={{ minHeight: 34, "& .MuiTabs-flexContainer": { gap: 0.5 } }}
        >
          {TABS.map((t, i) => (
            <Tab
              key={i}
              icon={t.icon}
              iconPosition="start"
              label={t.label}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.78rem",
                minHeight: 34,
                py: 0.6,
                px: 1.5,
                borderRadius: 1.5,
                color: "text.secondary",
                gap: 0.5,
                transition: "all 0.2s ease",
                "&.Mui-selected": {
                  color: "#fff",
                  background: `linear-gradient(135deg, ${t.tone}, ${C.secondary})`,
                  boxShadow: `0 4px 12px ${alpha(t.tone, 0.3)}`,
                },
              }}
            />
          ))}
        </Tabs>
      </Box>

      {tab === 0 && <LabTestsList />}
      {tab === 1 && <LabPackagesList />}
      {tab === 2 && <RedcliffeCatalogList />}
    </Box>
  );
}
